import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;

    // Check if the selected variant is unavailable (null = no matching variant, or available === false)
    const variant = event.detail.resource;
    const placeholderUrl = this.dataset.outOfStockPlaceholder;
    const isUnavailable = variant == null || variant.available === false;

    if (isUnavailable && placeholderUrl) {
      const placeholder = document.createElement('media-gallery');
      placeholder.className = this.className;
      placeholder.style.cssText = this.style.cssText;
      placeholder.dataset.outOfStockPlaceholder = placeholderUrl;
      placeholder.dataset.presentation = this.dataset.presentation || '';
      placeholder.innerHTML = `
        <div class="product-media-container" style="--media-preview-ratio: ${this.style.getPropertyValue('--gallery-aspect-ratio') || '3 / 4'};">
          <div class="product-media">
            <img
              src="${placeholderUrl}"
              alt="This variant is currently out of stock"
              loading="eager"
              style="width: 100%; height: 100%; object-fit: cover;"
            >
          </div>
        </div>
      `;
      this.replaceWith(placeholder);
      return;
    }

    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    // Preserve out-of-stock placeholder URL on the new gallery element
    if (placeholderUrl) {
      newMediaGallery.dataset.outOfStockPlaceholder = placeholderUrl;
    }

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
